/**
 * Hooks over restrainStore for the position UI: the active restrain for one
 * position (with a 1s clock so a timed restrain expires on screen), and the
 * buy/sell lock reasons for an order of a given size against it.
 */
import { useEffect, useState } from 'react';
import {
  findRestrainForPosition,
  restrainLockMessage,
  restrainLockOf,
  useRestrainStore,
  wouldChangeSize,
  type RestrainPosition,
  type RestrainVenue,
} from '../../store/restrainStore';
import { planManualOrder } from '../../utils/exitOnly';

/** Wall clock that ticks every second while `active`. */
export function useNow(active: boolean): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!active) return;
    setNow(Date.now());
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [active]);
  return now;
}

export interface RestrainTarget {
  brokerId?: string | null;
  accountId?: number | string | null;
  symbol?: string | null;
  /** Futures: a fresh Tradovate position often names only its contractId. */
  contractId?: number | string | null;
  netPos?: number | null;
  /** The chart's symbol, for a position that names no contract yet. */
  fallbackSymbol?: string | null;
}

/**
 * The active restrain for a displayed position, or null (flat, none, expired).
 * Subscribes to restrainStore, so it re-renders the moment rms:restrain lands.
 */
export function useRestrainFor(target: RestrainTarget | null | undefined): { entry: RestrainPosition | null; now: number } {
  const positions = useRestrainStore(s => s.positions);
  const now = useNow(positions.length > 0);
  if (!target || !target.netPos) return { entry: null, now };
  return { entry: findRestrainForPosition(positions, target, { chartSymbol: target.fallbackSymbol }, now), now };
}

export interface RestrainOrderLock {
  entry: RestrainPosition | null;
  /** Tooltip/refusal text when a Buy of `quantity` would change the size, else null. */
  buyLock: string | null;
  sellLock: string | null;
  /** Any size change at all is locked (adds, partial exits). */
  sizeLock: string | null;
  /** 1s clock, for the countdown on the locked button. */
  now: number;
}

export function useRestrainOrderLock(target: RestrainTarget | null | undefined, quantity: number): RestrainOrderLock {
  const { entry, now } = useRestrainFor(target);
  if (!entry || !target?.netPos) return { entry: null, buyLock: null, sellLock: null, sizeLock: null, now };
  const msg = restrainLockMessage(entry);
  const netPos = target.netPos;
  // The opposite side sends min(qty, |position|) (exit-only), so a qty over
  // the position is a full close, which restrain allows.
  const sent = (action: 'Buy' | 'Sell') => planManualOrder(netPos, action, quantity).quantity;
  return {
    entry,
    buyLock: wouldChangeSize(netPos, 'Buy', sent('Buy')) ? msg : null,
    sellLock: wouldChangeSize(netPos, 'Sell', sent('Sell')) ? msg : null,
    sizeLock: msg,
    now,
  };
}

/**
 * The running restrain that locks one venue's Restrain settings (off /
 * duration can't change until it ends), with a 1s clock for the countdown.
 * Null when that venue is off or armed and waiting for a position. Each venue
 * locks only itself: a running perps restrain never locks futures.
 */
export function useRestrainLock(venue: RestrainVenue = 'futures'): { lock: { symbol: string; endsAt: number | null } | null; now: number } {
  const enabled = useRestrainStore(s => s.venues[venue].enabled);
  const positions = useRestrainStore(s => s.positions);
  const now = useNow(enabled && positions.length > 0);
  return { lock: enabled ? restrainLockOf(positions, now, venue) : null, now };
}
